// @flow
import React, {
  // $FlowFixMe
  useMemo,
  // $FlowFixMe
  useEffect,
  // $FlowFixMe
  useContext,
  // $FlowFixMe
  useRef,
  // $FlowFixMe
  ReactNode
} from "react";
import { find, get, noop } from "lodash";
import { graphql } from "@apollo/client/react/hoc";
import { connect } from "react-redux";
import mediator from "@tvg/mediator";
import type { RaceWagerType, RaceProgram } from "@tvg/types/Race";
import { type WagerTypeProps } from "@fdr/types/ProgramPage";
import { WagerSelectorMask } from "@fdr/static-ui/Masks";
import {
  setBetType,
  setBetAmount,
  setSelectionRequest,
  setStartBetting,
  setWagerTypes
} from "@tvg/shared-actions/BetActions";
import WagerTypes from "@fdr/atomic-ui/_templates/WagerTypes";
import {
  isHongKongRaces,
  findWagerType,
  getSelectionsFromParams,
  findCompatibleWagerType,
  calcNodeListWidth
} from "@fdr/utils/wagerUtils";
import { fixedOffset } from "@fdr/utils/RaceHeaderUtils";
import { parseJSONCapiMessage } from "@fdr/utils/parseJSONCapiMessage";
import { FeedbackMessageContext } from "@fdr/utils/contextUtils";

import ApolloOptions from "./graphql/options.graph";
import WagerTypesQuery from "./graphql/queries/RaceWagerTypes.graphql";

export const WagerTypeSelector = (props: WagerTypeProps) => {
  const {
    userData: { logged: isLogged },
    wagerTypes,
    activeWagerType,
    race,
    withVideo,
    dispatch,
    isLoadingPP,
    location,
    isWinBetGroup,
    history,
    wagerList
  } = props;

  const wagerTypesScrollRef = useRef<ReactNode>(null);
  const { bannerProps } = useContext(FeedbackMessageContext);
  const withFixedBanner = !!bannerProps;

  useEffect(() => {
    dispatch(setWagerTypes(wagerTypes));
  }, [JSON.stringify(wagerTypes)]);

  const setDefaultBetAmount = (wt: ?RaceWagerType) => {
    const useCustomBetAmount = get(
      props,
      "userData.preferences.bet_prefs.useCustomBetAmount"
    );
    const picksDefault = [310, 330, 340, 350, 360, 370, 380, 390, 400];

    if (
      !isHongKongRaces(get(props, "currentRace.track.trackCode", "")) &&
      useCustomBetAmount &&
      isLogged
    ) {
      const betTypeId = +get(wt, "type.id", "");
      const betTypeGroupId = get(wt, "group.id", "");

      let defaultValue = 0;

      // Identify picks and daily doubles
      if (picksDefault.includes(betTypeId)) {
        defaultValue = +get(
          props,
          "userData.preferences.bet_prefs.default_bet_amount.P",
          0
        );
      } else {
        defaultValue = +get(
          props,
          `userData.preferences.bet_prefs.default_bet_amount["${betTypeGroupId}"]`,
          0
        );
      }

      props.dispatch(
        setBetAmount(
          defaultValue >= get(wt, "minWagerAmount", 100000)
            ? defaultValue
            : get(wt, "wagerAmounts[0]", 0)
        )
      );
    } else {
      props.dispatch(setBetAmount(get(wt, "wagerAmounts[0]", 0)));
    }
  };

  // load initial bet type
  useEffect(() => {
    if (wagerList.length > 0 && !activeWagerType) {
      dispatch(setBetType(wagerList[0]));
      setDefaultBetAmount(wagerList[0]);
    }
  }, []);

  // set default wager type
  useEffect(() => {
    if (wagerList.length > 0) {
      if (activeWagerType) {
        const newWager = find(
          wagerList,
          (wager) => wager.type.code === activeWagerType.type.code
        );
        if (newWager) {
          dispatch(setBetType(newWager));
          setDefaultBetAmount(newWager);
          return;
        }
      }

      dispatch(setBetType(wagerList[0]));
      setDefaultBetAmount(wagerList[0]);
    }
  }, [JSON.stringify(wagerList), JSON.stringify(activeWagerType)]);

  // Updates default bet amount when selected wager type changes
  useEffect(() => {
    if (activeWagerType) {
      setDefaultBetAmount(activeWagerType);
    }
  }, [JSON.stringify(activeWagerType)]);

  // update wager type con race change
  useEffect(() => {
    if (wagerList.length) {
      dispatch(setBetType(wagerList[0]));
      setDefaultBetAmount(wagerList[0]);
    }
  }, [JSON.stringify(wagerList)]);

  const sendGtmScrollEvent = (scrollValue: number) => {
    mediator.base.dispatch({
      type: "PPM_WAGER_SCROLL",
      payload: {
        label: scrollValue < 0 ? "left" : "right"
      }
    });
  };

  const onWagerTypeClick = (
    wagerType: RaceWagerType,
    lastWagerType: number,
    currentRace: RaceProgram
  ) => {
    if (wagerList) {
      const lastWager = wagerList.find(
        (wagerItem) => wagerItem.type.id === lastWagerType
      );

      if (wagerType !== activeWagerType) {
        mediator.base.dispatch({
          type: "PPM_WAGER_TYPE_SELECT",
          payload: {
            label: get(wagerType, "type.name"),
            betType: get(lastWager, "type.name"),
            raceNumber: get(currentRace, "raceNumber"),
            trackName: get(currentRace, "track.trackName")
          }
        });
        dispatch(setBetType(wagerType));
      }
    }
  };

  useEffect(() => {
    if (wagerList.length > 0 && !props.isLoading && window) {
      const urlParams = new URLSearchParams(window.location.search);
      const wagerTypeFromURL = urlParams.get("wt") || urlParams.get("betType");

      if (wagerTypeFromURL) {
        let wagerType: ?RaceWagerType = findWagerType(
          wagerTypeFromURL,
          wagerList
        );

        if (!wagerType) {
          wagerType = findCompatibleWagerType(wagerTypeFromURL, wagerList);
        }

        const wagerTypeIdx = wagerList.findIndex(
          (wt) => wt.type.code === wagerType?.type.code
        );

        if (wagerTypeIdx > 0 && wagerTypesScrollRef.current) {
          wagerTypesScrollRef.current.scroll({
            top: 0,
            left: calcNodeListWidth(
              wagerTypesScrollRef.current?.childNodes,
              wagerTypeIdx
            ),
            behavior: "smooth"
          });
        }

        if (activeWagerType && wagerType !== undefined) {
          dispatch(setBetType(wagerType));

          const selections = getSelectionsFromParams(urlParams);
          if (selections?.length > 0) {
            setTimeout(() => {
              dispatch(setSelectionRequest(selections));
              dispatch(setStartBetting(true));
              if (urlParams.has("bet")) {
                dispatch(setBetAmount(+urlParams.get("bet")));
              } else {
                setDefaultBetAmount(wagerType);
              }
            });
          }

          if (history) {
            history.replace(
              `${window.location.pathname}?race=${urlParams.get("race")}`
            );
          }
        }
      }
    }
  }, [
    props.isLoading,
    JSON.stringify(activeWagerType),
    JSON.stringify(wagerList),
    window?.location.search
  ]);

  return useMemo(() => {
    if (props.isLoading) return <WagerSelectorMask />;

    return wagerList.length > 0 ? (
      <WagerTypes
        ref={wagerTypesScrollRef}
        fixedOffset={fixedOffset(withVideo)}
        wagerTypes={wagerList}
        country={location.country}
        activeWagerType={get(activeWagerType, "type.id", 0)}
        onWagerTypeClick={(wagerType, lastWagerType) =>
          onWagerTypeClick(wagerType, lastWagerType, props.race)
        }
        withVideo={withVideo}
        scrollEventCallBack={sendGtmScrollEvent}
        showNameBetGroup={isWinBetGroup}
        withFixedBanner={withFixedBanner}
      />
    ) : null;
  }, [
    withVideo,
    isLoadingPP,
    props.isLoading,
    race.id,
    JSON.stringify(activeWagerType),
    JSON.stringify(wagerList),
    withFixedBanner
  ]);
};

WagerTypeSelector.defaultProps = {
  race: {},
  wagerProfile: "FDR-Generic",
  isLoading: true,
  wagerTypes: [],
  raceWagerTypes: [],
  location: {
    country: ""
  },
  activeWagerType: null,
  dispatch: noop,
  withVideo: false,
  userData: { logged: false },
  isLoadingPP: true,
  isWinBetGroup: false,
  history: { replace: noop },
  wagerList: []
};

export default connect((store) => ({
  activeWagerType: get(store, "RaceProgram.betType", null),
  wagerList: get(store, "RaceProgram.wagerTypes", []),
  userData: get(store, "userData"),
  isWinBetGroup: get(store, "capi.featureToggles.winBetGroup"),
  descriptionsWinBetGroup: parseJSONCapiMessage(
    store,
    "capi.messages.descriptionsWinBetGroup",
    {}
  )
}))(graphql(WagerTypesQuery, ApolloOptions)(WagerTypeSelector));
